import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Image as ImageIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useLang } from "../../context/LangContext";
import { API } from "../../utils/format";



const CategoriesManagement = () => {
  const { t, lang } = useLang();
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", name_en: "", description: "", sort_order: 0, logo_path: null });

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleSave = async () => {
    try {
      if (editCategory?.id) await axios.put(`${API}/categories/${editCategory.id}`, formData);
      else await axios.post(`${API}/categories`, formData);
      toast.success("Salvato");
      setIsDialogOpen(false);
      fetchCategories();
    } catch { toast.error("Errore"); }
  };

  const openEdit = (cat) => {
    setEditCategory(cat);
    setFormData({
      name: cat?.name || "", name_en: cat?.name_en || "",
      description: cat?.description || "", sort_order: cat?.sort_order || 0,
      logo_path: cat?.logo_path || null,
    });
    setIsDialogOpen(true);
  };

  const handleLogoUpload = async (cat, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/categories/${cat.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo aggiornato");
      fetchCategories();
    } catch { toast.error("Errore upload"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-semibold text-stone-900">{t.categories}</h2>
          <p className="text-stone-500 text-sm mt-1">{categories.length} categorie</p>
        </div>
        <Button onClick={() => { openEdit(null); }} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" />{t.new}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(cat => (
          <Card key={cat.id} className="bg-white border-stone-200 hover:border-[#C5A059] hover:shadow-md transition-all">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {cat.logo_path ? (
                      <img src={`${API}/files/${cat.logo_path}`} alt="" className="w-12 h-12 object-contain rounded border border-stone-200" />
                    ) : (
                      <div className="w-12 h-12 bg-stone-100 rounded border border-stone-200 flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-stone-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-stone-900">{cat.name}</h3>
                      {cat.name_en && <p className="text-xs text-stone-500">🇬🇧 {cat.name_en}</p>}
                    </div>
                  </div>
                  <p className="text-sm text-stone-500">{cat.description || "-"}</p>
                  <p className="text-xs text-stone-400 mt-1">{t.order}: {cat.sort_order || 0}</p>
                </div>
                <div className="flex gap-1">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(cat, e.target.files?.[0])} />
                    <span className="h-8 w-8 inline-flex items-center justify-center hover:bg-stone-100 rounded"><ImageIcon className="w-4 h-4 text-stone-500" /></span>
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(cat)} className="h-8 w-8 p-0 hover:bg-stone-100"><Edit className="w-4 h-4 text-stone-500" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => axios.delete(`${API}/categories/${cat.id}`).then(fetchCategories)} className="h-8 w-8 p-0 hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader><DialogTitle className="text-stone-900">{editCategory?.id ? t.edit : t.new} {t.category}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-stone-700 flex items-center gap-2">🇮🇹 Nome (IT)</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white border-stone-300" data-testid="category-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700 flex items-center gap-2">🇬🇧 Nome (EN)</Label>
              <Input value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} placeholder="English name (opzionale)" className="bg-white border-stone-300" data-testid="category-name-en-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700">Descrizione</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-white border-stone-300" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700">Ordine</Label>
              <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} className="bg-white border-stone-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-stone-300">{t.cancel}</Button>
            <Button onClick={handleSave} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="category-save-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default CategoriesManagement;

